/**
 * Расписания платежей и поступлений.
 *
 * Модель обязательства знала только «число месяца», поэтому годовая страховка
 * 60 000 ₽ считалась ежемесячной и входила в месячную нагрузку двенадцать раз
 * в год. Здесь появляется общее правило повторения: однократно либо каждые N
 * дней, недель, месяцев или лет.
 *
 * Главное свойство расчёта — **отсутствие дрейфа**. Наступление номер k
 * считается от исходной даты, а не от предыдущего наступления: платёж 31-го
 * числа в феврале переносится на 28-е, но в марте снова наступает 31-го.
 * Сложение «предыдущая дата плюс месяц» это свойство теряет и навсегда
 * сдвигает платёж на 28-е.
 */
import {
  addDays, addMonths, applyShift, daysInMonth, diffDays, isAfter, parts, ymd,
  type PlainDate, type ShiftRule,
} from './date.js';

export type ScheduleUnit = 'day' | 'week' | 'month' | 'year';

export interface PaymentSchedule {
  kind: 'once' | 'every';
  /**
   * Опорная дата. Для повторения хранит **исходный день месяца**: он не
   * перезаписывается посчитанной датой, иначе перенос с 31-го на 28-е стал бы
   * новым правилом.
   */
  anchor: PlainDate;
  /** Для `every`: положительное целое. */
  n?: number;
  unit?: ScheduleUnit;
  shiftRule: ShiftRule;
  /**
   * Начало действия. Без него опорная дата задаёт только **фазу**: платёж
   * 28-го числа, заведённый сегодня, имел наступления и в прошлых месяцах —
   * иначе просроченный платёж прошлого цикла исчез бы с экрана.
   * Указывается там, где расписание действительно началось в известный день.
   */
  from?: PlainDate;
  /** Необязательное окончание: дата или число наступлений от опорной. */
  until?: PlainDate;
  times?: number;
}

/** Раз в месяц по числу — прежнее поведение всех существующих обязательств. */
export const monthlyOn = (anchor: PlainDate, shiftRule: ShiftRule = 'exact'): PaymentSchedule =>
  ({ kind: 'every', n: 1, unit: 'month', anchor, shiftRule });

export const onceOn = (anchor: PlainDate, shiftRule: ShiftRule = 'exact'): PaymentSchedule =>
  ({ kind: 'once', anchor, shiftRule });

/**
 * Опорная дата для «числа месяца» из старой модели.
 *
 * Ищется ближайший к сегодняшнему дню месяц, в котором такое число вообще
 * существует. Для 31-го числа это важно буквально: взять 28 февраля значило бы
 * записать в расписание то, что является переносом, а не правилом.
 */
export function anchorForDayOfMonth(dayOfMonth: number, today: PlainDate): PlainDate {
  const day = Math.min(31, Math.max(1, Math.trunc(dayOfMonth)));
  const { year, month } = parts(today);
  for (let back = 0; back <= 12; back++) {
    const total = year * 12 + (month - 1) - back;
    const y = Math.floor(total / 12);
    const m = (total % 12) + 1;
    if (day <= daysInMonth(y, m)) return ymd(y, m, day);
  }
  // Недостижимо: хотя бы один месяц в году содержит любое число от 1 до 31.
  return ymd(year, month, Math.min(day, daysInMonth(year, month)));
}

const stepMonths = (s: PaymentSchedule): number =>
  (s.unit === 'year' ? 12 : 1) * Math.max(1, s.n ?? 1);

const stepDays = (s: PaymentSchedule): number =>
  (s.unit === 'week' ? 7 : 1) * Math.max(1, s.n ?? 1);

const monthsBetween = (from: PlainDate, to: PlainDate): number => {
  const a = parts(from), b = parts(to);
  return (b.year - a.year) * 12 + (b.month - a.month);
};

/** Дата k-го наступления **до** переноса с выходных. Считается от опорной. */
function rawOccurrence(s: PaymentSchedule, k: number): PlainDate {
  if (s.kind === 'once') return s.anchor;
  return s.unit === 'month' || s.unit === 'year'
    ? addMonths(s.anchor, k * stepMonths(s))
    : addDays(s.anchor, k * stepDays(s));
}

/** Сколько наступлений разрешено правилом окончания. */
const withinLimit = (s: PaymentSchedule, k: number): boolean =>
  s.times === undefined || k < s.times;

/**
 * Все наступления в отрезке `[from, to]` включительно.
 *
 * Возвращает даты после переноса с выходных, по возрастанию и без повторов:
 * два соседних наступления могут после переноса совпасть, и платить дважды в
 * один день человек не должен.
 */
export function occurrencesBetween(
  s: PaymentSchedule,
  from: PlainDate,
  to: PlainDate,
): PlainDate[] {
  if (isAfter(from, to)) return [];

  const out: PlainDate[] = [];
  const seen = new Set<string>();
  const push = (d: PlainDate): void => {
    if (isAfter(d, to) || isAfter(from, d)) return;
    if (s.from && isAfter(s.from, d)) return;
    if (s.until && isAfter(d, s.until)) return;
    if (seen.has(d)) return;
    seen.add(d);
    out.push(d);
  };

  if (s.kind === 'once') {
    push(applyShift(s.anchor, s.shiftRule));
    return out;
  }

  // Пропускаем заведомо ранние наступления, не перебирая их по одному:
  // недельный платёж, заведённый три года назад, иначе стоил бы 150 итераций.
  const behind = s.unit === 'month' || s.unit === 'year'
    ? Math.floor(monthsBetween(s.anchor, from) / stepMonths(s))
    : Math.floor(diffDays(s.anchor, from) / stepDays(s));
  // Отступаем на два шага назад: перенос с выходных может втащить в отрезок
  // наступление, которое по «сырой» дате в него не попадает.
  //
  // Индекс может быть отрицательным: опорная дата задаёт **фазу**, а не
  // начало. Иначе платёж 28-го числа, записанный сегодня, не имел бы прошлых
  // наступлений — и просроченный платёж прошлого цикла исчез бы с экрана.
  // Исключение — расписание с ограниченным числом повторений: там опорная
  // дата и есть первое наступление.
  // При ограничении по числу наступлений опорная дата и есть первое из них.
  let k = s.times === undefined ? behind - 2 : Math.max(0, behind - 2);

  // Предел — защита от бесконечного цикла при испорченном расписании.
  for (let guard = 0; guard < 10_000; guard++, k++) {
    if (k < 0) {
      // Сюда попадают только расписания без ограничения повторений.
      const early = applyShift(rawOccurrence(s, k), s.shiftRule);
      push(early);
      continue;
    }
    if (!withinLimit(s, k)) break;
    const raw = rawOccurrence(s, k);
    if (isAfter(raw, to) && isAfter(applyShift(raw, s.shiftRule), to)) break;
    push(applyShift(raw, s.shiftRule));
  }
  return out;
}

/** Ближайшее наступление не раньше указанной даты. `null`, если их больше нет. */
export function nextOccurrenceFrom(s: PaymentSchedule, from: PlainDate): PlainDate | null {
  // Год вперёд покрывает любое поддерживаемое правило, кроме совсем редких;
  // для них расширяем окно ещё на четыре года и на этом останавливаемся.
  for (const years of [1, 5]) {
    const found = occurrencesBetween(s, from, addMonths(from, years * 12));
    if (found.length > 0) return found[0]!;
  }
  return null;
}

/** Последнее наступление не позже указанной даты. `null`, если их ещё не было. */
export function lastOccurrenceUpTo(s: PaymentSchedule, to: PlainDate): PlainDate | null {
  for (const years of [1, 5]) {
    const found = occurrencesBetween(s, addMonths(to, -years * 12), to);
    if (found.length > 0) return found[found.length - 1]!;
  }
  return null;
}

/**
 * Справочная месячная нагрузка: сумма одной оплаты, приведённая к месяцу.
 *
 * Только для объяснения — «страховка стоит примерно 5 000 ₽ в месяц».
 * В расчёт денег идёт календарь наступлений, а не это число: в сентябре
 * платёж 60 000 ₽ реален, а в октябре его нет вовсе.
 */
export function monthlyEquivalentFactor(s: PaymentSchedule): number {
  if (s.kind === 'once') return 0;
  const n = Math.max(1, s.n ?? 1);
  switch (s.unit) {
    case 'day': return 30 / n;
    case 'week': return 30 / (7 * n);
    case 'year': return 1 / (12 * n);
    case 'month':
    default: return 1 / n;
  }
}

/** Человеческое название периодичности — для экрана и объяснений. */
export function scheduleLabel(s: PaymentSchedule): string {
  if (s.kind === 'once') return 'однократно';
  const n = Math.max(1, s.n ?? 1);
  if (s.unit === 'month' && n === 1) return 'ежемесячно';
  if (s.unit === 'month' && n === 3) return 'раз в квартал';
  if (s.unit === 'month' && n === 6) return 'раз в полгода';
  if (s.unit === 'year' && n === 1) return 'ежегодно';
  if (s.unit === 'week' && n === 1) return 'еженедельно';
  const unit = s.unit === 'day' ? 'дня' : s.unit === 'week' ? 'недели' : s.unit === 'year' ? 'года' : 'месяца';
  return `каждые ${n} ${unit}`;
}
